// Inline line discount for the Fashion till. Pure helpers: the discount itself
// is applied by Core (applyManualDiscount), these only pick the rule, preview
// the price and read back who approved it.

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const round2 = (v) => Math.round((v + Number.EPSILON) * 100) / 100;

export const LINE_DISCOUNT_REASONS = ["Damaged or soiled", "Loyal customer", "Price match", "Alteration delay"];

// The store's manual line rules: PD takes a percentage, FD a flat amount.
export function lineDiscountRules(types) {
  const list = Array.isArray(types) ? types : [];
  return {
    pct: list.find((t) => t?.type === "PD") || null,
    flat: list.find((t) => t?.type === "FD") || null,
  };
}

// A rule set up with its own value (10% Loyal) is applied at that value only,
// as Core's discount drawer locks it.
export function presetValue(rule) {
  const v = Number(rule?.discountType === "V" ? rule?.amountDiscount : rule?.percentageDiscount);
  return Number.isFinite(v) && v > 0 ? v : null;
}

// Line value before any discount: shelf price × pieces.
export const lineGross = (line) => round2(num(line?.realPrice ?? line?.salePrice) * Math.abs(num(line?.weight)));

// What the line costs after the discount, or an error to show instead.
export function previewLineDiscount(line, mode, value) {
  const gross = lineGross(line);
  const v = num(value);
  if (String(value ?? "").trim() === "" || v <= 0) return { error: "Enter a discount above zero." };
  if (mode === "pct" && v > 100) return { error: "A percentage can't be above 100." };
  const off = round2(mode === "pct" ? (gross * v) / 100 : v);
  if (off > gross) return { error: "The discount is more than the line." };
  return { gross, off, after: round2(gross - off) };
}

// "Approved by Priya, 14:05" for a manual line discount a manager approved.
export function approvalStamp(line) {
  const list = Array.isArray(line?.approval) ? line.approval : [];
  const rec = list.find((a) => a && a.mPricingrulesId === line?.mPricingruleId) || list.find((a) => a?.userName || a?.userId);
  if (!rec) return null;
  const who = rec.userName || "a manager";
  const time = typeof rec.time === "string" && rec.time.length >= 16 ? rec.time.slice(11, 16) : "";
  return `Approved by ${who}${time ? `, ${time}` : ""}`;
}

// True when the entered value falls in one of the rule's approval bands, so
// Core will ask for a manager when it is applied.
export function needsApproval(rule, value) {
  const v = Number(value);
  if (rule?.enableApproval !== "Y" || !Number.isFinite(v) || v <= 0) return false;
  const bands = Array.isArray(rule.mPricingApprovals) ? rule.mPricingApprovals : [];
  return bands.some((b) => {
    const min = Number(b?.min);
    const max = b?.max === null || b?.max === undefined || b?.max === "" ? Infinity : Number(b.max);
    return Number.isFinite(min) && v >= min && v <= max;
  });
}

// "Up to 10% without a manager." The approval band starts one step past the
// limit (10.01%), so the note names the limit the cashier can give alone.
export function approvalLimitNote(rule, currSymbol = "") {
  const bands = rule?.enableApproval === "Y" ? (rule.mPricingApprovals || []).filter((a) => Number.isFinite(Number(a?.min))) : [];
  if (bands.length === 0) return null;
  const lowest = bands.reduce((a, b) => (Number(b.min) < Number(a.min) ? b : a));
  const min = Number(lowest.min);
  if (min <= 0) return "A manager approves this discount.";
  const stepPast = Math.abs(Math.round(min * 100) % 100) === 1 && min > 1;
  const show = (v) => (lowest.type === "V" ? `${currSymbol}${v.toLocaleString("en-IN")}` : `${v}%`);
  return stepPast ? `Up to ${show(round2(min - 0.01))} without a manager.` : `Under ${show(min)} without a manager.`;
}

// What the bag line's chip says: "5% off · Price match" or "₹100 off · Loyal customer".
export function lineDiscountLabel(entry, currSymbol = "") {
  if (!entry || !(Number(entry.value) > 0)) return null;
  const amt = entry.mode === "pct" ? `${Number(entry.value)}% off` : `${currSymbol}${Number(entry.value).toLocaleString("en-IN")} off`;
  return entry.reason ? `${amt} · ${entry.reason}` : amt;
}
